import {isEmpty, get, isNil, isNaN} from 'lodash'
import {getSalesAnalysisDataSource} from 'selectors/salesContext'
import {getGlobalLayouts, getLayouts} from 'selectors/data'
import {permissions} from 'helpers/constants'
import {isOrdersLayoutType} from 'helpers/sales'
import {userHasPermission} from 'helpers/user'
import {isUUID} from "helpers/uuid"

export const layoutTypes = {
  ORDERS: 0,
  OPPORTUNITIES: 1,
  CONTACTS: 2,
  COMPANIES: 3,
  ACTIVITIES: 4,
  INVOICES: 5,
  PURCHASE_ORDERS: 6,
  PURCHASE_INVOICES: 7,
  DELIVERY_NOTES: 8,
}

export const defaultLayouts = {
  ORDERS: -1,
  INVOICES: -1,
}

export const LayoutPanelType = {
  BAR_CHART: 'BAR_CHART',
  COLUMN_CHART: 'COLUMN_CHART',
  LINE_CHART: 'LINE_CHART',
  PIE_CHART: 'PIE_CHART',
}

export const getLayoutTypeLabel = (layoutType) => {
  switch (layoutType) {
    case layoutTypes.ORDERS:
      return 'Quotes'
    case layoutTypes.OPPORTUNITIES:
      return 'Opportunities'
    case layoutTypes.CONTACTS:
      return 'Contacts'
    case layoutTypes.COMPANIES:
      return 'Companies'
    case layoutTypes.ACTIVITIES:
      return 'Activities'
    case layoutTypes.INVOICES:
      return 'Invoices'
    case layoutTypes.PURCHASE_ORDERS:
      return 'Purchase Orders'
    case layoutTypes.PURCHASE_INVOICES:
      return 'Purchase Invoices'
    case layoutTypes.DELIVERY_NOTES:
      return 'Delivery Notes'
  }
}

export const getSalesAnalysisInvoiceFiltersFromLayout = (layoutFilters) => {
  return {
    statusTypes: getLayoutFilterValue(getLayoutFilter(layoutFilters, 'statusTypes')),
    companies: getCompanyMultiListValues(layoutFilters),
    userTypes: getMultiListFieldValues(layoutFilters, 'userTypes'),
    showDormants: getLayoutBoolValue(getLayoutFilter(layoutFilters, 'showDormants'), false),
    contacts: getMultiListFieldValues(layoutFilters, 'contacts'),
    timeKey: getLayoutFilterValue(getLayoutFilter(layoutFilters, 'timeKey')),
    orderTypeId: getMultiListFieldValues(layoutFilters, 'orderTypeId'),
    targetStart: getLayoutNumberValue(getLayoutFilter(layoutFilters, 'targetStart')),
    targetEnd: getLayoutNumberValue(getLayoutFilter(layoutFilters, 'targetEnd')),
  }
}

export const getSalesAnalysisQuoteFiltersFromLayout = (layoutFilters = []) => {
  return {
    statusTypes: getMultiListFieldValues(layoutFilters, 'statusTypes'),
    companies: getCompanyMultiListValues(layoutFilters),
    userTypes: getMultiListFieldValues(layoutFilters, 'userTypes'),
    showDormants: getLayoutBoolValue(getLayoutFilter(layoutFilters, 'showDormants'), false),
    contacts: getMultiListFieldValues(layoutFilters, 'contacts'),
    timeKey: getLayoutFilterValue(getLayoutFilter(layoutFilters, 'timeKey')),
    orderTypeId: getMultiListFieldValues(layoutFilters, 'orderTypeId'),
    targetStart: getLayoutNumberValue(getLayoutFilter(layoutFilters, 'targetStart')),
    targetEnd: getLayoutNumberValue(getLayoutFilter(layoutFilters, 'targetEnd')),
    dateTypes: getMultiListFieldValues(layoutFilters, 'dateTypes'),
  }
}

export const getSalesAnalysisPivotTableParamsFromLayout = layout => {
  const {layoutGroups, layoutFilters} = layout
  return {
    groups: getLayoutGroups(layoutGroups),
    sortColumn: getLayoutFilterValue(getLayoutFilter(layoutFilters, 'sortColumn')),
    sortDirection: getLayoutFilterValue(getLayoutFilter(layoutFilters, 'sortDirection')),
    search: getLayoutFilterValue(getLayoutFilter(layoutFilters, 'search')),
    columns: getMultiListFieldValues(layoutFilters, 'selectedFields', ';'),
  }
}

export const getDefaultLayoutIdForDataSource = dataSource => {
  const key = getDataSourceKey(dataSource)

  return defaultLayouts[key]
}

export const getDefaultLayoutForDataSource = dataSource => {
  return isOrdersLayoutType(dataSource)
      ? getSalesAnalysisQuoteFiltersFromLayout()
      : getSalesAnalysisInvoiceFiltersFromLayout()
}

export const getLayoutOptions = (globalLayouts, layouts) => {
  const options = []

  const myLayouts = getMyLayouts(layouts)
  const sharedLayouts = getSharedLayouts(layouts)

  if (!isEmpty(myLayouts))
    options.push({
      label: 'My Layouts',
      options: mapLayoutsToOptions(myLayouts),
    })

  if (!isEmpty(sharedLayouts))
    options.push({
      label: 'Shared Layouts',
      options: mapLayoutsToOptions(sharedLayouts),
    })

  if (!isEmpty(globalLayouts))
    options.push({
      label: 'Default Layouts',
      options: mapLayoutsToOptions(globalLayouts),
    })

  return options
}

export const mapLayoutsToOptions = layouts => {
  return layouts.map(layout => {
    return {
      label: get(layout, 'name', null),
      value: get(layout, 'id', null),
      key: get(layout, 'id', null),
      layout,
    }
  })
}

export const getMyLayouts = data => {
  return data.filter(({private: isPrivate}) => isPrivate)
}

export const getSharedLayouts = data => {
  return data.filter(({global, private: isPrivate}) => !global && !isPrivate)
}

export const getLayoutsForDataSource = (state, isGlobal = false) => {
  const dataSource = getSalesAnalysisDataSource(state)
  const layouts = getLayouts(state)
  const globalLayouts = getGlobalLayouts(state)

  if (isGlobal) return globalLayouts.filter(layout => layout.type === dataSource)

  return layouts.filter(layout => layout.type === dataSource)
}

export const getSelectedLayout = (layoutId, layouts, dataSource) => {
  if (isNil(layoutId))
    layoutId = getDefaultLayoutIdForDataSource(dataSource)

  const layout = layouts.find(layout => layout.id === layoutId && layout.type === dataSource)

  if (isNil(layout))
    return getDefaultLayoutForDataSource(dataSource)

  return layout
}

export const getLayoutFilter = (layoutFilters = [], key) => {
  return layoutFilters.find((layoutFilter) => layoutFilter.key === key)
}

export const getLayoutFilterValue = (layoutFilter, defaultValue = null) => {
  return get(layoutFilter, 'value', defaultValue)
}

export const getLayoutNumberValue = (layoutFilter, defaultValue = null) => {
  const value = getLayoutFilterValue(layoutFilter)

  if (isEmptyLayoutFilterValue(value)) return defaultValue

  const number = Number(value)

  return isNaN(number)
    ? defaultValue
    : number
}

export const getLayoutBoolValue = (layoutFilter, defaultValue = false) => {
  const bool = get(layoutFilter, 'value', defaultValue)

  return bool === "true"
}

export const getCompanyMultiListValues = (layoutFilters) => {
  const multiListValues = getLayoutFilterValue(getLayoutFilter(layoutFilters, 'companies'))

  if (isEmpty(multiListValues))
    return []

  return multiListValues
      .split(';')
      .map(companyValue => {
        const values = companyValue.split(':')
        return {
          value: values[0],
          label: values[1],
        }
      })
}

export const getMultiListFieldValues = (layoutFilters, key, separator = ',') => {
  const multiListValues = getLayoutFilterValue(getLayoutFilter(layoutFilters, key))

  if (isEmpty(multiListValues))
    return []

  return multiListValues
      .split(separator)
      .map(multiListValue => {
        return !isNaN(parseInt(multiListValue))
            ? parseInt(multiListValue)
            : multiListValue
      })
}

export const getDataSourceKey = (value) => {
  return Object.keys(layoutTypes).find(key => layoutTypes[key] === value)
}

export const getFiltersFromLayout = (data) => {
  const {layout, dataSource} = data
  const layoutFilters = get(layout, 'layoutFilters', [])

  return isOrdersLayoutType(dataSource)
      ? getSalesAnalysisQuoteFiltersFromLayout(layoutFilters)
      : getSalesAnalysisInvoiceFiltersFromLayout(layoutFilters)
}

export const getLayoutGroups = groups => {
  if (isEmpty(groups))
    return []

  return groups.map(group => group.key)
}

export const getLayoutPermissions = (layout, user, isOverriding) => {
  const isAdmin = userHasPermission(user, permissions.MANAGE_SALES_LAYOUTS)
  const isPrivate = layout.private || layout.isPrivate
  const isGlobal = layout.global || layout.isGlobal

  return {
    saveDisabled: !isOverriding,
    updateDisabled: !isOverriding || isGlobal || (!isPrivate && !isAdmin),
    deleteDisabled: isGlobal || (!isPrivate && !isAdmin),
    shareDisabled: isGlobal || !isAdmin,
    renameDisabled: isGlobal || (!isPrivate && !isAdmin),
  }
}

export const getCompanyLayoutFilters = (companies) => {
  if (isEmpty(companies))
    return []

  return companies
      .map(company => {
        const value = get(company, 'value', null)
        const label = get(company, 'label', null)
        return `${value}:${label}`
      })
      .join(';')
}

export const getStatusLayoutFilters = (params, key) => {
  const value = get(params, key, null)

  return isNil(value) ? value : value.toString()
}

export const getLayoutFilterValues = (params, key, separator = ',') => {
  const paramsValue = get(params, key, [])
  return isEmpty(paramsValue)
      ? []
      : paramsValue.join(separator)
}

export const isEmptyLayoutFilterValue = (value) => {
  if (typeof value === 'number') return isNaN(value)
  return isEmpty(value)
}

export const getNonEmptyLayoutFilters = (layoutFilters) => {
  return layoutFilters.filter(layoutFilter => !isEmptyLayoutFilterValue(layoutFilter.value))
}

export const getLayoutGroupsValues = (layoutId, layoutGroups) => {
  return !isEmpty(layoutGroups) ?
      layoutGroups
          .map((group, index) => {
            return {
              layoutId,
              key: group,
              level: index,
            }
          })
      : []
}

export const isShowExitPrompt = (layout, user, isOverriding) => {
  const permissions = getLayoutPermissions(layout, user, isOverriding)
  const isUpdateDisabled = Boolean(permissions.updateDisabled)
  const isGlobal = layout.global || layout.isGlobal

  return isOverriding && !isUpdateDisabled && !isGlobal
}

export const removeTemporaryIdsForLayout = (layout) => {
  return {
    ...layout,
    layoutPanels: layout.layoutPanels.map(removeTemporaryIdForLayoutPanel),
  }
}

export const removeTemporaryIdForLayoutPanel = (layoutPanel) => {
  return {
    ...layoutPanel,
    id: isUUID(layoutPanel.id)
        ? null
        : layoutPanel.id,
  }
}